import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useMakeSelector, useSelector } from 'hooks/redux'
import * as yup from 'yup'
import { compose, sortBy, map, prop } from 'ramda'

import { makeGetInputOptionsFiltered } from 'selectors/feature/inputs'
import { getOutputConfigAssessment } from 'selectors/feature/outputs'

import strings from 'utils/strings'

import { StyledSelectMenu } from './InputSelector.style'

const mapOption = ({ uri, description }) => ({
  key: uri,
  value: uri,
  title: description,
})

const sortInputOptions = compose(sortBy(prop('title')), map(mapOption))

const InputSelector = ({
  onInputChange,
  selectedType,
  signalUri,
  outputId,
  ...props
}) => {
  const outputConfig = useSelector(getOutputConfigAssessment, { id: outputId })
  const inputOptions = useMakeSelector(makeGetInputOptionsFiltered, {
    isSixteenBitOutput: false,
    hasNativeOutput: false,
    subType: selectedType,
    isOutputBoolean: true, // check validity of arg in selector
    isSingleCondition: true,
    outputConfig,
  })

  const mapped = useMemo(() => sortInputOptions(inputOptions), [inputOptions])

  const filterOption = useCallback(
    (inputValue, option) => {
      const found = mapped.find((o) => o.value === option.props.value)
      if (!found) return true
      return (
        (found.title || '').toUpperCase().indexOf(inputValue.toUpperCase()) !==
        -1
      )
    },
    [mapped]
  )

  // Generate key from options to force Select remount when options change.
  // Fixes Ant Design 3.x Select not updating dropdown when options prop changes.
  const optionsKey = useMemo(
    () => inputOptions.map((opt) => `${opt.uri}:${opt.description}`).join(','),
    [inputOptions]
  )

  return (
    <StyledSelectMenu
      key={optionsKey}
      showSearch
      label={strings.input}
      name={strings.select}
      placeholder={strings.select}
      value={signalUri}
      onChange={onInputChange}
      className="form-field"
      options={mapped}
      filterOption={filterOption}
      optionLabelProp="title"
      rules={yup.string().nullable().required('Required')}
    />
  )
}

InputSelector.propTypes = {
  onInputChange: PropTypes.func,
  selectedType: PropTypes.string,
  signalUri: PropTypes.string,
  outputId: PropTypes.string,
}

export default InputSelector
